import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import {
  fetchCountryFeatures,
  saveCountry,
  updateCountryFeatures,
  type Country,
  type CountryFeature,
} from './api';

type Props = {
  /** Edit when set, create otherwise. */
  country?: Country;
  onClose: () => void;
};

const FeaturesEditor = ({
  countryId,
  features,
}: {
  countryId: number;
  features: CountryFeature[];
}) => {
  const [available, setAvailable] = useState(
    () => new Set(features.filter((f) => f.is_available).map((f) => f.feature_id)),
  );
  const [saved, setSaved] = useState(false);

  const { errors, save, isPending } = useSaveForm(
    (ids: number[]) => updateCountryFeatures(countryId, ids),
    () => setSaved(true),
  );

  const toggle = (featureId: number) => {
    setSaved(false);
    setAvailable((prev) => {
      const next = new Set(prev);
      if (!next.delete(featureId)) next.add(featureId);
      return next;
    });
  };

  return (
    <div>
      <hr />
      <h5>Условия тарификации</h5>
      {features.map((feature) => (
        <div key={feature.feature_id} className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={available.has(feature.feature_id)}
              onChange={() => toggle(feature.feature_id)}
            />
            {feature.description}
          </label>
        </div>
      ))}
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
      {saved && <span className="text-green">Данные сохранены успешно </span>}
      <button
        type="button"
        className="btn btn-success"
        disabled={isPending}
        onClick={() => save([...available])}
      >
        Обновить
      </button>
    </div>
  );
};

export const CountryModal = ({ country, onClose }: Props) => {
  const qc = useQueryClient();
  const [name, setName] = useState(country?.name ?? '');
  const [phoneCode, setPhoneCode] = useState(country?.phone_code ?? '');

  const features = useQuery({
    queryKey: ['country-features', country?.id],
    queryFn: () => fetchCountryFeatures((country as Country).id),
    enabled: country !== undefined,
    gcTime: 0,
  });

  const { errors, clearErrors, save, isPending } = useSaveForm(saveCountry, () => {
    void qc.invalidateQueries({ queryKey: ['place-countries'] });
    onClose();
  });

  return (
    <ModalDialog
      onClose={onClose}
      isSaving={isPending}
      onSave={() => save({ id: country?.id, name, phoneCode })}
    >
      <FormField label="Название" errors={errors.name}>
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder="Название..."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearErrors('name');
          }}
        />
      </FormField>
      {country && (
        <FormField label="Трехсимвольный код страны" errors={errors.phone_code}>
          <input
            className="form-control input-sm"
            style={{ width: 250 }}
            placeholder="Трехсимвольный код страны..."
            value={phoneCode}
            onChange={(e) => {
              setPhoneCode(e.target.value);
              clearErrors('phone_code');
            }}
          />
        </FormField>
      )}
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
      {country && features.data && features.data.length > 0 && (
        <FeaturesEditor countryId={country.id} features={features.data} />
      )}
    </ModalDialog>
  );
};
